import { useState } from 'react';

import { Modal } from '@/components/Modal';
import { BOND_KIND_LABEL, useSubmitBond } from '@/lib/contractSigning';
import { useSubcontracts } from '@/lib/queries';
import type { GuaranteeBondKind, Uuid } from '@/lib/types';

const KINDS: GuaranteeBondKind[] = ['CONTRACT', 'ADVANCE', 'DEFECT', 'PAYMENT'];

/** 보증증권 제출 (V629) — 증권 파일 한 장과 종류 · 발행처 · 번호 · 금액 · 기간. 본사가 확인한다. */
export function BondSubmitModal({ siteId, onClose }: { siteId: Uuid; onClose: () => void }) {
  const { data: contracts = [] } = useSubcontracts(siteId);
  const submit = useSubmitBond(siteId);
  const [contractId, setContractId] = useState('');
  const [kind, setKind] = useState<GuaranteeBondKind>('CONTRACT');
  const [issuer, setIssuer] = useState('');
  const [bondNo, setBondNo] = useState('');
  const [amount, setAmount] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const chosen = contractId || contracts[0]?.id || '';

  const send = () => {
    if (!chosen) return setMessage('계약을 골라 주세요.');
    if (!file) return setMessage('증권 파일을 붙여 주세요.');
    const form = new FormData();
    form.append('subcontractId', chosen);
    form.append('kind', kind);
    if (issuer.trim()) form.append('issuer', issuer.trim());
    if (bondNo.trim()) form.append('bondNo', bondNo.trim());
    if (amount) form.append('amount', amount);
    if (startsOn) form.append('startsOn', startsOn);
    if (endsOn) form.append('endsOn', endsOn);
    form.append('file', file);
    setMessage(null);
    submit.mutate(form, {
      onSuccess: onClose,
      onError: (cause) => setMessage(cause instanceof Error ? cause.message : '내지 못했습니다.'),
    });
  };

  const shown = amount === '' ? '' : Number(amount).toLocaleString('ko-KR');

  return (
    <Modal
      title="보증증권 제출"
      onClose={onClose}
      maxWidth="30rem"
      actions={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            닫기
          </button>
          <button type="button" className="btn-primary" onClick={send} disabled={submit.isPending}>
            {submit.isPending ? '내는 중…' : '제출'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-ui-xs">
        <label className="flex flex-col gap-1">
          <span style={{ color: 'var(--text-secondary)' }}>계약</span>
          <select className="input-field" value={chosen} onChange={(event) => setContractId(event.target.value)}>
            {contracts.length === 0 && <option value="">계약 없음</option>}
            {contracts.map((contract) => (
              <option key={contract.id} value={contract.id}>
                {[contract.trade, contract.workName].filter(Boolean).join(' · ')}
              </option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap gap-2">
          {KINDS.map((one) => (
            <button key={one} type="button" className={one === kind ? 'btn-primary' : 'btn-secondary'} onClick={() => setKind(one)}>
              {BOND_KIND_LABEL[one]}
            </button>
          ))}
        </div>
        <label className="flex flex-col gap-1">
          <span style={{ color: 'var(--text-secondary)' }}>발행처</span>
          <input className="input-field" value={issuer} onChange={(event) => setIssuer(event.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span style={{ color: 'var(--text-secondary)' }}>증권 번호</span>
          <input className="input-field" value={bondNo} onChange={(event) => setBondNo(event.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span style={{ color: 'var(--text-secondary)' }}>보증 금액</span>
          <input
            className="input-field text-right"
            inputMode="numeric"
            value={shown}
            onChange={(event) => setAmount(event.target.value.replace(/[^0-9]/g, ''))}
          />
        </label>
        <div className="flex gap-2">
          <label className="flex flex-1 flex-col gap-1">
            <span style={{ color: 'var(--text-secondary)' }}>보증 시작</span>
            <input className="input-field" type="date" value={startsOn} onChange={(event) => setStartsOn(event.target.value)} />
          </label>
          <label className="flex flex-1 flex-col gap-1">
            <span style={{ color: 'var(--text-secondary)' }}>보증 끝</span>
            <input className="input-field" type="date" value={endsOn} onChange={(event) => setEndsOn(event.target.value)} />
          </label>
        </div>
        <label className="flex flex-col gap-1">
          <span style={{ color: 'var(--text-secondary)' }}>증권 파일</span>
          <input type="file" accept="application/pdf,image/*" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
        </label>
        {message && <div style={{ color: 'var(--danger, #dc2626)' }}>{message}</div>}
      </div>
    </Modal>
  );
}
